/**
 * ============================================
 * Authentication Service - Store Admin
 * ============================================
 *
 * Handles JWT token acquisition and management for the Store Admin site.
 *
 * Scope: Store Admin authenticates with Bearer tokens only - the client
 * token below. It intentionally does NOT use the customer SSO session (the
 * httpOnly refresh cookie from uniquegiftapi /auth/*): no request here sends
 * credentials/cookies, business/login and business/verifyOtp don't issue
 * that session, and the API's jwtAuth keeps client tokens working unchanged.
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_APIURL 
  ? process.env.REACT_APP_APIURL.replace('/api/', '') 
  : 'http://localhost:5000';
const TOKEN_KEY = 'client_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

const authService = {
  /**
   * Initialize JWT token on app startup
   */
  async initializeToken() {
    try {
      // Check if we have a valid token
      const existingToken = this.getToken();
      const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

      // If token exists and hasn't expired, use it
      if (existingToken && tokenExpiry) {
        const expiryTime = new Date(tokenExpiry);
        const now = new Date();

        // If token expires in more than 1 hour, use existing token
        if (expiryTime > now && (expiryTime - now) > 3600000) {
          console.log('[AUTH] Using existing valid token');
          return existingToken;
        }
      }

      // Fetch new token from backend
      console.log('[AUTH] Fetching new JWT token from backend...');
      const response = await axios.post(`${API_BASE_URL}/auth/get-client-token`);
      const token = response.data.token;

      // Validate token format
      if (!token || typeof token !== 'string' || token.split('.').length !== 3) {
        throw new Error('Invalid token format received from backend');
      }

      // Calculate expiry time (24 hours from now)
      const expiryTime = new Date();
      expiryTime.setHours(expiryTime.getHours() + 24);

      // Store token and expiry
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toISOString());

      console.log('[AUTH] JWT token obtained and stored successfully');
      console.log('[AUTH] Token expires at:', expiryTime.toISOString());

      return token;
    } catch (error) {
      console.error('[AUTH ERROR] Failed to initialize token:', error);
      // If token fetch fails, clear any existing tokens
      this.clearToken();
      throw new Error('Failed to authenticate with backend. Please try again.');
    }
  },

  /**
   * Get current token from localStorage
   */
  getToken() {
    const token = localStorage.getItem(TOKEN_KEY);
    
    // Validate token format if it exists
    if (token && (typeof token !== 'string' || token.split('.').length !== 3)) {
      console.warn('[AUTH] Invalid token format detected in localStorage, clearing...');
      this.clearToken();
      return null;
    }
    
    return token;
  },

  /**
   * Clear token from localStorage
   */
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    console.log('[AUTH] Token cleared from storage');
  },

  /**
   * Check if token is expired
   */
  isTokenExpired() {
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!tokenExpiry) {
      return true;
    }

    const expiryTime = new Date(tokenExpiry);
    const now = new Date();
    return expiryTime <= now;
  },

  /**
   * Refresh token if expired or invalid
   */
  async refreshTokenIfNeeded() {
    try {
      const currentToken = this.getToken();
      
      // If no token exists, get a new one
      if (!currentToken) {
        console.log('[AUTH] No token found, fetching new token...');
        return await this.initializeToken();
      }
      
      // Validate token format (JWT should have 3 parts separated by dots)
      if (typeof currentToken !== 'string' || currentToken.split('.').length !== 3) {
        console.log('[AUTH] Invalid token format detected, clearing and fetching new token...');
        this.clearToken();
        return await this.initializeToken();
      }
      
      // Check if token is expired
      if (this.isTokenExpired()) {
        console.log('[AUTH] Token expired, fetching new token...');
        return await this.initializeToken();
      }
      
      return currentToken;
    } catch (error) {
      // If there's any error with the token, clear it and get a new one
      console.log('[AUTH] Token error, clearing and fetching new token...', error.message);
      this.clearToken();
      return await this.initializeToken();
    }
  },

  /**
   * Force refresh token (useful when token is corrupted)
   */
  async forceRefreshToken() {
    console.log('[AUTH] Force refreshing token...');
    this.clearToken();
    return await this.initializeToken();
  },

  /**
   * Validate token format
   */
  isValidTokenFormat(token) {
    return token && typeof token === 'string' && token.split('.').length === 3;
  },

  /**
   * Clear corrupted tokens and get fresh token
   */
  async handleCorruptedToken() {
    console.log('[AUTH] Handling corrupted token - clearing localStorage and fetching new token');
    this.clearToken();
    return await this.initializeToken();
  }
};

export default authService;
